import { supabase } from '../lib/supabase';
import {
  transformSessionFromDB,
  transformSessionToDB,
  transformPlayerFromDB,
  transformPlayerToDB,
  transformExpenseFromDB,
  transformExpenseToDB,
  transformMasterPlayerFromDB,
  transformMasterPlayerToDB,
} from '../utils/dataTransform';

/**
 * Sessions Service
 */
export const sessionsService = {
  // Get all sessions for the current user
  // RLS policies automatically filter by user_id, so we don't need to filter manually
  async getAll() {
    const { data, error } = await supabase
      .from('sessions')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    // RLS policies ensure only the current user's sessions are returned
    return (data || []).map(transformSessionFromDB);
  },

  // Get a single session by ID
  async getById(sessionId) {
    const { data, error } = await supabase
      .from('sessions')
      .select('*')
      .eq('id', sessionId)
      .single();
    
    if (error) throw error;
    return transformSessionFromDB(data);
  },

  // Create a new session
  async create(sessionData) {
    // Get current user ID from Supabase session
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated to create a session');
    }

    const dbData = transformSessionToDB(sessionData);
    dbData.user_id = user.id; // Tag session to the current user
    
    const { data, error } = await supabase
      .from('sessions')
      .insert([dbData])
      .select()
      .single();
    
    if (error) throw error;
    return transformSessionFromDB(data);
  },

  // Update a session
  async update(sessionId, updates) {
    const dbData = transformSessionToDB(updates);
    dbData.last_updated = new Date().toISOString();
    
    const { data, error } = await supabase
      .from('sessions')
      .update(dbData)
      .eq('id', sessionId)
      .select()
      .single();
    
    if (error) throw error;
    return transformSessionFromDB(data);
  },

  // Delete a session (and cascade delete related data)
  async delete(sessionId) {
    // First delete related players
    await supabase
      .from('players')
      .delete()
      .eq('session_id', sessionId);
    
    // Delete related expenses
    await supabase
      .from('expenses')
      .delete()
      .eq('session_id', sessionId);
    
    // Then delete the session
    const { error } = await supabase
      .from('sessions')
      .delete()
      .eq('id', sessionId);
    
    if (error) throw error;
  },

  // Archive a session
  async archive(sessionId) {
    const { data, error } = await supabase
      .from('sessions')
      .update({ is_active: false, last_updated: new Date().toISOString() })
      .eq('id', sessionId)
      .select()
      .single();
    
    if (error) throw error;
    return transformSessionFromDB(data);
  },

  // Restore an archived session
  async restore(sessionId) {
    const { data, error } = await supabase
      .from('sessions')
      .update({ is_active: true, last_updated: new Date().toISOString() })
      .eq('id', sessionId)
      .select()
      .single();
    
    if (error) throw error;
    return transformSessionFromDB(data);
  },
};

/**
 * Players Service
 */
export const playersService = {
  // Get all players for a session
  // RLS policies automatically filter by user_id
  async getBySession(sessionId) {
    if (!sessionId) {
      throw new Error('Session ID is required to fetch players');
    }

    const { data, error } = await supabase
      .from('players')
      .select('*')
      .eq('session_id', sessionId) // Filter by session - all players tied to this session
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    // RLS policies ensure only the current user's players are returned
    // session_id filter ensures only players for this specific session
    return (data || []).map(transformPlayerFromDB);
  },

  // Get a single player by ID
  async getById(playerId) {
    const { data, error } = await supabase
      .from('players')
      .select('*')
      .eq('id', playerId)
      .single();
    
    if (error) throw error;
    return transformPlayerFromDB(data);
  },

  // Create a new player
  async create(sessionId, playerData) {
    // Validate sessionId is provided
    if (!sessionId) {
      throw new Error('Session ID is required to create a player');
    }

    // Get current user ID from Supabase session
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated to create a player');
    }

    const dbData = transformPlayerToDB(playerData);
    dbData.session_id = sessionId; // Tag player to the session
    dbData.user_id = user.id; // Tag player to the current user
    
    const { data, error } = await supabase
      .from('players')
      .insert([dbData])
      .select()
      .single();
    
    if (error) throw error;
    return transformPlayerFromDB(data);
  },

  // Update a player
  async update(playerId, updates) {
    const dbData = transformPlayerToDB(updates);
    
    const { data, error } = await supabase
      .from('players')
      .update(dbData)
      .eq('id', playerId)
      .select()
      .single();
    
    if (error) throw error;
    return transformPlayerFromDB(data);
  },

  // Delete a player
  async delete(playerId) {
    const { error } = await supabase
      .from('players')
      .delete()
      .eq('id', playerId);
    
    if (error) throw error;
  },

  // Clear all players for a session
  async clearAll(sessionId) {
    const { error } = await supabase
      .from('players')
      .delete()
      .eq('session_id', sessionId);
    
    if (error) throw error;
  },
};

/**
 * Expenses Service
 */
export const expensesService = {
  // Get all expenses for a session
  // RLS policies automatically filter by user_id
  async getBySession(sessionId) {
    if (!sessionId) {
      throw new Error('Session ID is required to fetch expenses');
    }

    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .eq('session_id', sessionId) // Filter by session - all expenses tied to this session
      .order('timestamp', { ascending: false });
    
    if (error) throw error;
    // RLS policies ensure only the current user's expenses are returned
    // session_id filter ensures only expenses for this specific session
    return (data || []).map(transformExpenseFromDB);
  },

  // Get a single expense by ID
  async getById(expenseId) {
    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .eq('id', expenseId)
      .single();
    
    if (error) throw error;
    return transformExpenseFromDB(data);
  },

  // Create a new expense
  async create(sessionId, expenseData) {
    // Validate sessionId is provided
    if (!sessionId) {
      throw new Error('Session ID is required to create an expense');
    }

    // Get current user ID from Supabase session
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated to create an expense');
    }

    const dbData = transformExpenseToDB(expenseData);
    dbData.session_id = sessionId; // Tag expense to the session
    dbData.user_id = user.id; // Tag expense to the current user
    
    const { data, error } = await supabase
      .from('expenses')
      .insert([dbData])
      .select()
      .single();
    
    if (error) throw error;
    return transformExpenseFromDB(data);
  },

  // Update an expense
  async update(expenseId, updates) {
    const dbData = transformExpenseToDB(updates);
    
    const { data, error } = await supabase
      .from('expenses')
      .update(dbData)
      .eq('id', expenseId)
      .select()
      .single();
    
    if (error) throw error;
    return transformExpenseFromDB(data);
  },

  // Delete an expense
  async delete(expenseId) {
    const { error } = await supabase
      .from('expenses')
      .delete()
      .eq('id', expenseId);
    
    if (error) throw error;
  },

  // Clear all expenses for a session
  async clearAll(sessionId) {
    const { error } = await supabase
      .from('expenses')
      .delete()
      .eq('session_id', sessionId);
    
    if (error) throw error;
  },
};

/**
 * Master Players Service
 */
export const masterPlayersService = {
  // Get all master players for the current user
  // RLS policies automatically filter by user_id
  async getAll() {
    const { data, error } = await supabase
      .from('master_players')
      .select('*')
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    // RLS policies ensure only the current user's master players are returned
    return (data || []).map(transformMasterPlayerFromDB);
  },

  // Get a single master player by ID
  async getById(playerId) {
    const { data, error } = await supabase
      .from('master_players')
      .select('*')
      .eq('id', playerId)
      .single();
    
    if (error) throw error;
    return transformMasterPlayerFromDB(data);
  },

  // Create a new master player
  async create(playerData) {
    // Get current user ID from Supabase session
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated to create a master player');
    }

    const dbData = transformMasterPlayerToDB(playerData);
    dbData.user_id = user.id; // Tag master player to the current user
    
    const { data, error } = await supabase
      .from('master_players')
      .insert([dbData])
      .select()
      .single();
    
    if (error) throw error;
    return transformMasterPlayerFromDB(data);
  },

  // Update a master player
  async update(playerId, updates) {
    const dbData = transformMasterPlayerToDB(updates);
    dbData.updated_at = new Date().toISOString();
    
    const { data, error } = await supabase
      .from('master_players')
      .update(dbData)
      .eq('id', playerId)
      .select()
      .single();
    
    if (error) throw error;
    return transformMasterPlayerFromDB(data);
  },

  // Delete a master player
  async delete(playerId) {
    const { error } = await supabase
      .from('master_players')
      .delete()
      .eq('id', playerId);
    
    if (error) throw error;
  },

  // Clear all master players
  async clearAll() {
    const { error } = await supabase
      .from('master_players')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all (using a condition that's always true)
    
    if (error) throw error;
  },
};

